import { useEffect, useState } from "react";
import { addDays, addWeeks, addMonths, format, formatDistanceToNow } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { BellOff, Loader2 } from "lucide-react";
import { SnoozeData } from "@/hooks/useSnooze";

const DURATION_OPTIONS = [
  { label: "1 day", getValue: () => addDays(new Date(), 1).toISOString() },
  { label: "1 week", getValue: () => addWeeks(new Date(), 1).toISOString() },
  { label: "1 month", getValue: () => addMonths(new Date(), 1).toISOString() },
  { label: "Until date...", getValue: () => "pick_date" as const },
  { label: "Indefinitely", getValue: () => null },
] as const;

const REASON_OPTIONS = [
  { value: "not_urgent", label: "Not urgent right now" },
  { value: "being_addressed", label: "Already being addressed separately" },
  { value: "too_many_notifications", label: "Too many notifications" },
  { value: "handed_off", label: "Handed off to another tech" },
] as const;

const REASON_LABELS: Record<string, string> = Object.fromEntries(
  REASON_OPTIONS.map((o) => [o.value, o.label])
);

type SnoozeSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetName: string;
  onSnooze: (end: string | null, reason: string | null) => void;
  isPending: boolean;
  onLift?: () => void;
  isLiftPending?: boolean;
  activeSnooze?: SnoozeData | null;
  /** When set, the manage step shows this message instead of a lift button (e.g. location-level snooze) */
  locationLevelMessage?: string | null;
};

export const SnoozeSheet = ({
  open,
  onOpenChange,
  targetName,
  onSnooze,
  isPending,
  onLift,
  isLiftPending = false,
  activeSnooze,
  locationLevelMessage,
}: SnoozeSheetProps) => {
  const initialStep = activeSnooze ? "manage" : "duration";

  const [selectedEnd, setSelectedEnd] = useState<string | null | "pick_date">(null);
  const [pickedDate, setPickedDate] = useState<Date | undefined>(undefined);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [step, setStep] = useState<"manage" | "duration" | "reason">(initialStep);

  // Reset step when sheet opens/closes or activeSnooze changes
  useEffect(() => {
    if (open) {
      setStep(activeSnooze ? "manage" : "duration");
      setSelectedEnd(null);
      setPickedDate(undefined);
      setSelectedReason(null);
    }
  }, [open, activeSnooze]);

  const handleReset = () => {
    setSelectedEnd(null);
    setPickedDate(undefined);
    setSelectedReason(null);
    setStep(activeSnooze ? "manage" : "duration");
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) handleReset();
    onOpenChange(isOpen);
  };

  const handleDurationSelect = (value: string | null | "pick_date") => {
    if (value === "pick_date") {
      setSelectedEnd("pick_date");
      return;
    }
    setSelectedEnd(value);
    setStep("reason");
  };

  const handleDateConfirm = () => {
    if (!pickedDate) return;
    setSelectedEnd(pickedDate.toISOString());
    setStep("reason");
  };

  const handleConfirm = () => {
    const endValue = selectedEnd === "pick_date" ? null : selectedEnd;
    onSnooze(endValue, selectedReason);
  };

  const resolvedEnd =
    selectedEnd === "pick_date"
      ? pickedDate
        ? pickedDate.toISOString()
        : null
      : selectedEnd;

  const snoozeEndText = activeSnooze?.end
    ? `until ${format(new Date(activeSnooze.end), "MMM d, yyyy")} (${formatDistanceToNow(new Date(activeSnooze.end), { addSuffix: false })} remaining)`
    : "indefinitely";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {step === "manage" ? `Manage snooze — ${targetName}` : `Snooze ${targetName}`}
          </SheetTitle>
          <SheetDescription>
            {step === "manage"
              ? "This item is currently snoozed. You can lift the snooze or change the duration."
              : "Mute notifications for this item. This only affects your view."}
          </SheetDescription>
        </SheetHeader>

        {/* Manage existing snooze */}
        {step === "manage" && activeSnooze && (
          <div className="flex flex-col gap-3 pt-4">
            <div className="flex items-center gap-3 rounded-lg border border-snooze-purple-border bg-snooze-purple-light px-3 py-3">
              <BellOff size={16} className="text-snooze-purple shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-snooze-purple">
                  Snoozed {snoozeEndText}
                </p>
                {activeSnooze.reason && (
                  <p className="text-slate-500 mt-0.5">
                    Reason: {REASON_LABELS[activeSnooze.reason] ?? activeSnooze.reason}
                  </p>
                )}
              </div>
            </div>

            {locationLevelMessage ? (
              <p className="text-sm text-slate-500 text-center py-1">
                {locationLevelMessage}
              </p>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => onLift?.()}
                  disabled={isLiftPending}
                  className="w-full"
                >
                  {isLiftPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Lifting...
                    </>
                  ) : (
                    "Lift snooze"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep("duration")}
                  className="text-sm text-snooze-purple hover:text-snooze-purple/80 font-medium text-center py-1"
                >
                  Change duration
                </button>
              </>
            )}
          </div>
        )}

        {/* Duration selection */}
        {step === "duration" && (
          <div className="flex flex-col gap-2 pt-4">
            <p className="text-sm font-medium text-slate-700">How long?</p>
            <div className="flex flex-col gap-1.5">
              {DURATION_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => handleDurationSelect(option.getValue())}
                  className="text-left px-3 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
                >
                  {option.label}
                </button>
              ))}
            </div>

            {selectedEnd === "pick_date" && (
              <div className="flex flex-col items-center gap-2 pt-2">
                <Calendar
                  mode="single"
                  selected={pickedDate}
                  onSelect={setPickedDate}
                  disabled={{ before: addDays(new Date(), 1) }}
                />
                <Button
                  variant="thalo"
                  size="sm"
                  onClick={handleDateConfirm}
                  disabled={!pickedDate}
                >
                  {pickedDate
                    ? `Snooze until ${format(pickedDate, "MMM d, yyyy")}`
                    : "Select a date"}
                </Button>
              </div>
            )}

            {activeSnooze && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep("manage")}
                className="mt-1"
              >
                Back
              </Button>
            )}
          </div>
        )}

        {/* Reason selection */}
        {step === "reason" && (
          <div className="flex flex-col gap-2 pt-4">
            <p className="text-sm font-medium text-slate-700">
              Reason{" "}
              <span className="font-normal text-slate-400">(optional)</span>
            </p>
            <div className="flex flex-col gap-1.5">
              {REASON_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setSelectedReason(
                      selectedReason === option.value ? null : option.value
                    )
                  }
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    selectedReason === option.value
                      ? "border-snooze-purple bg-snooze-purple-light text-slate-800 font-medium"
                      : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep("duration")}
                disabled={isPending}
              >
                Back
              </Button>
              <Button
                variant="thalo"
                size="sm"
                onClick={handleConfirm}
                disabled={isPending}
                className="flex-1"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Snoozing...
                  </>
                ) : resolvedEnd ? (
                  `Snooze until ${format(new Date(resolvedEnd), "MMM d, yyyy")}`
                ) : (
                  "Snooze indefinitely"
                )}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
